import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/apiClient";
import { useCurrentAppUser } from "../lib/useCurrentAppUser";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import type { MessageRecord, UserProfile } from "@shared/types";

/** One thread — modeled on EscalationsPage.tsx's reply composer. */
export function MessageThreadPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: me } = useCurrentAppUser();
  const [other, setOther] = useState<UserProfile | null>(null);
  const [messages, setMessages] = useState<MessageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards against a slower-to-resolve load() for a conversation the user
  // has since navigated away from overwriting the screen with the wrong
  // person's profile/messages once it finally settles.
  const activeUserIdRef = useRef(userId);

  function load() {
    if (!userId) return;
    activeUserIdRef.current = userId;
    const requestUserId = userId;
    setLoading(true);
    Promise.all([
      api.get<{ profile: UserProfile }>(`/api/people/${userId}`),
      api.get<{ messages: MessageRecord[] }>(`/api/messages/${userId}`),
    ])
      .then(([profileData, messagesData]) => {
        if (activeUserIdRef.current !== requestUserId) return;
        setOther(profileData.profile);
        setMessages(messagesData.messages);
      })
      .catch((err) => {
        if (activeUserIdRef.current !== requestUserId) return;
        setError(err instanceof ApiError ? err.message : "Failed to load this conversation.");
      })
      .finally(() => {
        if (activeUserIdRef.current === requestUserId) setLoading(false);
      });
  }

  useEffect(load, [userId]);

  async function send() {
    const body = draft.trim();
    if (!body || !userId) return;
    setSending(true);
    setError(null);
    try {
      await api.post("/api/messages", { recipientId: userId, body });
      setDraft("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to send your message.");
    } finally {
      setSending(false);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (error && !other) return <p className="error-text">{error}</p>;

  return (
    <div className="content-col">
      <button className="link-btn" onClick={() => navigate("/messages")}>
        ← Back to Messages
      </button>

      <h1 style={{ margin: 0 }}>{other?.fullName ?? other?.email}</h1>

      <div className="card message-thread-card">
        {messages.length === 0 ? (
          <p className="muted">No messages yet — say hello.</p>
        ) : (
          <div className="message-list">
            {messages.map((m) => {
              const mine = m.senderId === me?.id;
              return (
                <div className={mine ? "message-bubble mine" : "message-bubble"} key={m.id}>
                  <div className="message-bubble-body">{m.body}</div>
                  <div className="message-bubble-meta">{formatRelativeTime(m.createdAt)}</div>
                </div>
              );
            })}
          </div>
        )}

        {error && <p className="error-text">{error}</p>}
        <div className="escalation-reply">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Write a message…" />
          <div className="escalation-reply-actions">
            <button className="btn btn-primary" disabled={sending || !draft.trim()} onClick={send}>
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
